/**
 * Guided-planning inputs for the shared account-capture screen: the asset types saved in the session,
 * guided funding sources (a snapshot once the session is complete), a completed session's allocations
 * for the "(−amount)" badges, partner-share records kept in the session, and legacy partner shares.
 * On a finalized session the first account save reopens it (`useReopenSession`).
 */
import { useMemo } from 'react';

import { useFundingSourcesState, type PersistedSelection } from '@/features/funding-sources';
import {
  useIsSessionComplete,
  useSession,
  useSessionMutations,
} from '@/features/guided-financial-planning/context';
import { useFundingSources } from './use-funding-sources';
import { useLegacyPartnerAllocationSelections } from './use-legacy-partner-allocation-selections';
import { useReopenSession } from './use-reopen-session';

export function useGuidedAccountCapture() {
  const { session, isLoading } = useSession();
  const { updateStepData } = useSessionMutations();
  const isSessionComplete = useIsSessionComplete();
  const { reopen, isReopening } = useReopenSession();
  const fundingSources = useFundingSources();
  const legacyPartnerSelections = useLegacyPartnerAllocationSelections();
  const { isSaving, saveError } = useFundingSourcesState();

  const fundingAllocations = session?.stepData?.planComparisonSnapshot?.fundingAllocations;
  const completedAllocations = useMemo(() => {
    if (!isSessionComplete) return undefined;
    return (fundingAllocations ?? []).reduce<Record<string, number>>((acc, allocation) => {
      acc[allocation.selfFundingSourceId] = allocation.amount;
      return acc;
    }, {});
  }, [isSessionComplete, fundingAllocations]);

  return {
    selectedTypes: session?.stepData?.selectedAssets ?? [],
    fundingSources,
    isLoading,
    isSaving: isSaving || isReopening,
    saveError,
    readOnly: isReopening,
    beforeAccountSave: isSessionComplete ? () => reopen() : undefined,
    completedAllocations,
    savedAllocationSelections: session?.stepData?.partnerAllocationSelections as
      | Record<string, PersistedSelection>
      | undefined,
    persistAllocationSelections: (selections: Record<string, PersistedSelection>) =>
      updateStepData({ partnerAllocationSelections: selections }),
    legacyPartnerSelections,
  };
}
